import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

const PolicyDetailPage = () => {
  const { policyId } = useParams();

  const policies = {
    'terms': {
      title: 'Terms of Service',
      lastUpdated: 'April 2026',
      content: `
## 1. Platform Description & Marketplace Liability Shield

**IMPORTANT: Mechanicease is a technology platform that connects customers with independent automotive service providers (mechanics). Mechanicease does NOT provide automotive repair services directly.**

The platform facilitates communication, booking, quoting, and payment processing between customers and mechanics. Mechanicease acts solely as an intermediary marketplace and is not a party to the service agreement between customers and mechanics.

By using this platform, you acknowledge and agree that:
- Mechanicease is a **marketplace platform only**
- Mechanicease does **not** employ mechanics
- Mechanicease does **not** perform, supervise, or control repair work
- Mechanicease does **not** guarantee the quality, safety, or outcome of any service

## 2. Agreement to Terms

**You must explicitly agree to these Terms of Service before booking any service through the platform.**

By creating an account, browsing mechanics, or booking a service, you confirm that you have read, understood, and agree to be bound by these Terms of Service.

## 3. Eligibility

Users must be at least 18 years old and capable of entering into legally binding agreements to use this platform.

## 4. Independent Contractor Agreement

### For Mechanics:

**All mechanics on the Mechanicease platform are independent contractors, NOT employees of Mechanicease.**

By registering as a mechanic on this platform, you acknowledge and agree that:

- You are an **independent contractor** operating your own business
- You are **solely responsible** for the quality, safety, and legality of your work
- You maintain your own business licenses, certifications, and insurance
- You determine your own working hours, methods, and pricing
- You are responsible for your own taxes, benefits, and business expenses
- **Mechanicease has no control over how you perform your services**
- You will sign a separate Independent Contractor Agreement upon approval

**Mechanic Responsibility for Work Quality:**
The mechanic is solely responsible for:
- All repair work performed
- Accuracy of diagnoses and recommendations
- Quality of parts used
- Safety of the vehicle after service
- Any damages or injuries resulting from their work

Mechanicease is not liable for any claims arising from the mechanic's services.

## 5. Customer Responsibilities

Customers agree to:
- Provide accurate vehicle and service information
- Communicate honestly with mechanics
- **Review and sign quotes before service begins**
- Pay for services that are agreed upon
- Comply with these Terms of Service

## 6. Written Quote Requirement (Legal Protection)

**IMPORTANT: No service shall be finalized or commenced until the customer has reviewed and signed (electronically or physically) the quote provided by the mechanic.**

- Mechanics must provide a written quote before beginning any work
- The quote must itemize labor hours, parts, and estimated costs
- **Customers must acknowledge/sign the quote** before service begins
- Any changes to the quote during service require customer approval
- Unsigned work may not be covered under platform protections

This protects both parties and ensures clear agreement on scope and pricing.

## 7. Mechanic Standards & No Unnecessary Delays

Mechanics agree to:
- Provide accurate service descriptions and time estimates
- Maintain necessary certifications and insurance
- Perform services professionally and safely
- Follow platform policies and quality standards
- **Complete repairs within agreed timelines without unnecessary delays**
- **Provide clear explanations for any extended repair times**
- Notify customers immediately of any delays with valid reasons
- Not hold vehicles longer than necessary without customer approval
- Provide daily updates for repairs exceeding 24 hours

Violation of this policy may result in penalties and removal from the platform.

## 8. Pricing and Payments

Mechanics determine their own pricing unless otherwise stated.

Payments are processed through the Mechanicease platform to ensure transparency and security. All transactions are recorded for dispute resolution purposes.

Invoices must clearly show:
- Labor hours and rate
- Itemized parts with costs
- Applicable taxes
- Total amount due

## 9. Reviews and Ratings

Customers may leave reviews and ratings following completed services. The platform may remove reviews that are fraudulent, abusive, or misleading.

## 10. Dispute Resolution & Liability Cap

### Dispute Resolution Process:
1. Customers and mechanics should first attempt to resolve disputes directly
2. Either party may request platform mediation
3. Mechanicease may assist in mediation but is not obligated to resolve disputes

### **Limitation of Liability (Dispute Cap Clause):**

**TO THE MAXIMUM EXTENT PERMITTED BY LAW:**

- Mechanicease's total liability in any single dispute shall NOT exceed the value of the specific job booked through the platform
- Mechanicease is NOT liable for any indirect, incidental, special, consequential, or punitive damages
- Mechanicease is NOT liable for damages resulting from services performed by independent mechanics
- Mechanicease is NOT liable for any personal injury, property damage, or vehicle damage resulting from mechanic services

**You acknowledge that mechanics are independent contractors and Mechanicease bears no responsibility for their work.**

## 11. Indemnification

You agree to indemnify and hold harmless Mechanicease, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:
- Your use of the platform
- Your violation of these Terms
- Any services you provide or receive through the platform
- Any dispute between customer and mechanic

## 12. Account Suspension

The platform may suspend or terminate accounts that violate these terms or engage in fraudulent or harmful behavior.

## 13. Governing Law

These Terms shall be governed by the laws of the Province of Ontario, Canada, without regard to conflict of law principles.

## 14. Changes to Terms

These Terms of Service may be updated at any time. Continued use of the platform indicates acceptance of updated terms. Material changes will be communicated via email or platform notification.

## 15. Severability

If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.

## 16. Contact

For questions regarding these terms please contact:

**Mechanicease Legal**
legal@mechanicease.ca
support@mechanicease.ca
      `
    },
    'privacy': {
      title: 'Privacy Policy',
      lastUpdated: 'January 2025',
      content: `
Mechanicease respects your privacy and is committed to protecting your personal information in compliance with the **Personal Information Protection and Electronic Documents Act (PIPEDA)**.

## 1. Information We Collect
We may collect information including:
- Name
- Email address
- Phone number
- Location/Postal Code
- Vehicle information
- Service history
- Payment details (processed by third-party providers)

## 2. Purpose of Collection
Your information is collected for the following purposes:
- Connect customers with mechanics
- Process service requests and bookings
- Improve platform performance
- Prevent fraud and abuse
- Communicate service updates
- Track repair progress and timelines

**We only collect information that is necessary for these purposes.**

## 3. Consent
By using Mechanicease, you consent to the collection, use, and disclosure of your personal information as described in this policy.

You may withdraw your consent at any time by contacting us, subject to legal or contractual restrictions.

## 4. Marketing Communications
We may send you promotional emails about our services. You can opt-out of marketing communications at any time by:
- Clicking "Unsubscribe" in any marketing email
- Updating your preferences in your account settings
- Contacting us at privacy@mechanicease.ca

**Note:** You will still receive transactional emails related to your bookings and account.

## 5. Data Sharing
We may share necessary information with mechanics in order to fulfill service requests.

**We do not sell personal customer data.**

Aggregated or anonymized data may be used to improve our services and analyze repair trends.

**Note:** Mechanic contact information is only shared with customers after a booking is confirmed to protect both parties.

## 6. Data Security
We implement reasonable safeguards to protect personal data from unauthorized access, disclosure, or misuse, including:
- Encryption of sensitive data
- Secure server infrastructure
- Access controls and authentication
- Regular security assessments

## 7. Data Retention
We retain personal information only as long as necessary to fulfill the purposes for which it was collected, or as required by law.

## 8. Your Rights Under PIPEDA
You have the right to:
- **Access** your personal information held by us
- **Correct** any inaccurate or incomplete information
- **Withdraw consent** for certain uses of your information
- **Request deletion** of your personal information (subject to legal requirements)
- **File a complaint** with the Office of the Privacy Commissioner of Canada

## 9. Third-Party Services
We use third-party services for payment processing and analytics. These services have their own privacy policies and we encourage you to review them.

## 10. Policy Updates
This policy may be updated periodically to reflect changes in services or legal requirements. We will notify you of significant changes via email or platform notification.

## 11. Contact
For privacy inquiries, access requests, or complaints, please contact:

**Mechanicease Privacy Officer**
privacy@mechanicease.ca

This policy complies with the **Personal Information Protection and Electronic Documents Act (PIPEDA)** as enforced by the Office of the Privacy Commissioner of Canada.
      `
    },
    'refund': {
      title: 'Refund & Dispute Policy',
      lastUpdated: 'January 2025',
      content: `
Our goal is to ensure fair and transparent service experiences.

## 1. Customer Concerns
If a customer believes a service was performed improperly or pricing was unfair, they should contact Mechanicease within **7 days** of service completion.

## 2. Dispute Review
The platform may review relevant information including:
- Invoices with itemized labor and parts
- Photos (before and after)
- Communication records
- Mechanic responses
- Repair timeline and any delays

## 3. Resolution
Possible resolutions may include:
- Service correction by the mechanic
- Mediation between customer and mechanic
- Platform review and mechanic accountability

## 4. Delay-Related Disputes
For disputes related to **unnecessary delays**:
- Customers may report mechanics who hold vehicles without valid reasons
- Mechanics must provide documentation for any extended repair times
- Repeated delay violations may result in mechanic removal from the platform

## 5. Platform Role
Mechanicease acts as a facilitator and does not guarantee specific repair outcomes.

Final resolution decisions will depend on the circumstances of each case.

## 6. Abuse
False claims or abuse of the dispute process may result in account suspension.

## 7. Contact
For dispute inquiries please contact:

**Mechanicease**
disputes@mechanicease.ca
      `
    },
    'acceptable-use': {
      title: 'Acceptable Use Policy',
      lastUpdated: 'January 2025',
      content: `
Mechanicease is committed to maintaining a safe and trustworthy marketplace.

## User Conduct
By using this platform, users agree not to:
- Provide false or misleading information
- Post fraudulent reviews or ratings
- Attempt to bypass the platform to avoid fees
- Harass or threaten other users
- Engage in illegal or unsafe activities
- Upload malicious software or attempt to disrupt platform operations
- Contact mechanics directly using information obtained before booking

## Mechanic Conduct
Mechanics agree not to:
- Misrepresent qualifications or certifications
- Provide dishonest diagnostics or pricing
- Pressure customers into unnecessary repairs
- Use customer information for unauthorized marketing
- **Hold vehicles longer than necessary without proper explanation**
- **Delay repairs to increase labor charges**
- Solicit customers to conduct business outside the platform

## No Unnecessary Delays Policy
Mechanics must:
- Provide accurate repair time estimates
- Complete work within quoted timeframes
- Notify customers immediately of any delays
- Document reasons for extended repairs
- Not hold vehicles as leverage for additional charges

## Enforcement
Violation of this policy may result in:
- Warning and performance review
- Temporary account suspension
- Permanent removal from the platform

Mechanicease reserves the right to investigate and take action against any activity that harms the integrity of the platform.
      `
    },
    'mechanic-standards': {
      title: 'Mechanic Standards',
      lastUpdated: 'January 2025',
      content: `
To protect customers and maintain high service quality, mechanics on our platform must meet strict standards.

## Verification
Mechanics are required to provide:
- Identity verification
- Relevant certifications
- Proof of insurance
- Professional references

## Service Quality
Mechanics must:
- Provide honest diagnostics
- Use appropriate parts
- Communicate clearly with customers
- Follow safe repair practices
- **Complete repairs without unnecessary delays**

## No Unnecessary Delays Commitment
All Mechanicease mechanics commit to:
- Providing accurate repair time estimates upfront
- Completing work within the quoted timeframe
- Notifying customers immediately of any unexpected issues
- Never holding a vehicle longer than necessary
- Providing daily status updates for extended repairs

## Performance Metrics
We track key performance indicators for all mechanics:
- **On-Time Completion Rate**: Percentage of jobs completed within estimated time
- **Response Time**: Average time to respond to booking requests
- **Customer Satisfaction**: Based on reviews and ratings
- **Dispute Rate**: Frequency of customer complaints
- **No-Show Rate**: Missed or cancelled appointments

## Transparency
Mechanics must provide:
- Repair documentation
- Before and after photos (when applicable)
- Clear invoice breakdowns with itemized labor and parts
- Receipts for parts purchased

## Customer Feedback
Customer reviews are monitored to maintain high standards. Mechanics who repeatedly fail to meet expectations may be removed from the platform.

## Our Mission
We are building a trusted network of automotive professionals who value honesty, transparency, and customer satisfaction above all else.

**No unnecessary delays. No surprises. No hidden fees.**
      `
    },
    'data-usage': {
      title: 'Data Usage & Analytics Policy',
      lastUpdated: 'January 2025',
      content: `
Mechanicease collects service-related information in order to improve platform functionality and enhance customer experience.

## Information Collected
This information may include:
- Vehicle type and model
- Repair categories
- Service pricing
- Service frequency
- Customer feedback
- Repair timelines and completion rates

## Personal Data Protection
Personal information such as names, phone numbers, and addresses will **not be sold** to third parties.

## Aggregated Data Usage
Aggregated and anonymized data may be used for purposes including:
- Service quality analysis
- Repair pricing insights
- Industry research
- Platform improvements
- Identifying mechanics with delay patterns

## Analytics and Insights
We use data to:
- Improve repair time estimates
- Identify top-performing mechanics
- Detect patterns of delays or issues
- Enhance customer matching with mechanics

## Third-Party Sharing
Any analytics shared externally will not include personally identifiable customer information.

## User Acknowledgment
By using the platform, users acknowledge that anonymized service data may be used to improve services and develop insights.

## Contact
For data inquiries please contact:

**Mechanicease**
data@mechanicease.ca
      `
    }
  };

  const policy = policies[policyId];

  if (!policy) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Policy not found</h1>
          <Button asChild>
            <Link to="/policies">View All Policies</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-1">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <h2 key={index} className="font-['Barlow'] text-xl font-bold text-slate-900 mt-8 mb-4">
            {trimmedLine.replace('## ', '')}
          </h2>
        );
      } else if (trimmedLine.startsWith('- ')) {
        const listContent = trimmedLine.replace('- ', '');
        const hasBold = listContent.includes('**');
        if (hasBold) {
          const parts = listContent.split('**');
          currentList.push(
            <li key={index} className="text-slate-600">
              {parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-slate-900">{part}</strong> : part)}
            </li>
          );
        } else {
          currentList.push(<li key={index} className="text-slate-600">{listContent}</li>);
        }
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-1">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <p key={index} className="text-slate-900 font-bold mt-4 mb-2">
            {trimmedLine.replace(/\*\*/g, '')}
          </p>
        );
      } else if (trimmedLine) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-1">{currentList}</ul>);
          currentList = [];
        }
        const hasBold = trimmedLine.includes('**');
        if (hasBold) {
          const parts = trimmedLine.split('**');
          elements.push(
            <p key={index} className="text-slate-600 mb-4">
              {parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-slate-900">{part}</strong> : part)}
            </p>
          );
        } else {
          elements.push(<p key={index} className="text-slate-600 mb-4">{trimmedLine}</p>);
        }
      }
    });

    if (currentList.length > 0) {
      elements.push(<ul key="final-list" className="list-disc pl-6 mb-4 space-y-1">{currentList}</ul>);
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>
            <Button variant="outline" asChild>
              <Link to="/policies">
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Policies
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-4">
            <Shield className="w-4 h-4 text-orange-500" />
            <span className="text-orange-500 text-sm font-medium">Legal Document</span>
          </div>
          <h1 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-white mb-2">
            {policy.title}
          </h1>
          <p className="text-slate-400">Last updated: {policy.lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg border border-slate-200 p-8 md:p-12">
            {renderContent(policy.content)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">© 2024 Mechanicease. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PolicyDetailPage;
